import React, { useRef, useState, useEffect } from "react";
import { Icon } from "@cloudscape-design/components";
import "./horizontal-card-container.scss";

interface HorizontalCardContainerProps {
  children: React.ReactNode;
  gap?: number;
  className?: string;
  ariaLabel?: string;
}

export default function HorizontalCardContainer({
  children,
  gap = 16,
  className = "",
  ariaLabel = "Horizontal card container"
}: HorizontalCardContainerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Card width + gap for scroll amount
  const scrollAmount = 360 + gap;

  const updateScrollButtons = () => {
    if (containerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  const scrollLeft = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const scrollRight = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      updateScrollButtons();
      container.addEventListener('scroll', updateScrollButtons);
      
      // Update on resize
      const resizeObserver = new ResizeObserver(updateScrollButtons);
      resizeObserver.observe(container);

      return () => {
        container.removeEventListener('scroll', updateScrollButtons);
        resizeObserver.disconnect();
      };
    }
  }, [children]);

  return (
    <div 
      className={`horizontal-card-container ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      aria-label={ariaLabel}
    >
      {/* Left Arrow */}
      <div 
        className={`scroll-arrow scroll-arrow-left ${isHovered && canScrollLeft ? 'visible' : ''} ${!canScrollLeft ? 'disabled' : ''}`}
        onClick={canScrollLeft ? scrollLeft : undefined}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && canScrollLeft) {
            e.preventDefault();
            scrollLeft();
          }
        }}
        role="button"
        tabIndex={canScrollLeft ? 0 : -1}
        aria-label="Scroll left"
        data-testid="scroll-button"
      >
        <Icon name="angle-left" />
      </div>

      {/* Scrollable Container */}
      <div 
        ref={containerRef}
        className="scroll-container"
        style={{ gap: `${gap}px` }}
      >
        {children}
      </div>

      {/* Right Arrow */}
      <div 
        className={`scroll-arrow scroll-arrow-right ${isHovered && canScrollRight ? 'visible' : ''} ${!canScrollRight ? 'disabled' : ''}`}
        onClick={canScrollRight ? scrollRight : undefined}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && canScrollRight) {
            e.preventDefault();
            scrollRight();
          }
        }}
        role="button"
        tabIndex={canScrollRight ? 0 : -1}
        aria-label="Scroll right"
        data-testid="scroll-button"
      >
        <Icon name="angle-right" />
      </div>
    </div>
  );
}
